
import { HubConnection, HubConnectionState } from '@microsoft/signalr'

import { Component, Vue, Watch } from 'vue-property-decorator'
import { Action, Mutation, State } from 'vuex-class'

import api from '@/apiClient'

import { UPDATE_WEBSOCKET_STATUS } from '@/store/mutations'
import { RootState } from '@/store/state'

@Component
export default class WebSocket extends Vue {
  connection: HubConnection | null = null

  invoiceEvents = [
    ['ordering.invoice.closed', 'invoiceClosed'],
    ['ordering.invoice.closing', 'invoiceClosing'],
    ['ordering.invoice.created', 'invoiceCreated'],
    ['ordering.invoice.updated', 'invoiceUpdated']
  ]

  orderEvents = [
    ['ordering.order.approved', 'orderApproved'],
    ['ordering.order.authorized', 'orderAuthorized'],
    ['ordering.order.canceled', 'orderCanceled'],
    ['ordering.order.completed', 'orderCompleted'],
    ['ordering.order.started', 'orderStarted']
  ]

  paymentMethodEvents = [
    ['payment.payment-method.added', 'paymentMethodAdded'],
    ['payment.payment-method.updated', 'paymentMethodUpdated'],
    ['payment.payment-method.removed', 'paymentMethodRemoved']
  ]

  @State((state: RootState) => state.auth.tokenV2)
  readonly tokenV2!: string | null

  @Watch('tokenV2', { immediate: true })
  onTokenChange () {
    if (this.tokenV2 === null) {
      this.closeConnection()
    } else {
      this.openConnection()
    }
  }

  @Action('ordering/loadInvoice')
  loadInvoice!: (id: number) => Promise<void>

  @Action('ordering/loadOrder')
  loadOrder!: (id: number) => Promise<void>

  @Action('paymentMethods/fetchAllowedPaymentMethod')
  loadAllowedPaymentMethod!: (id: number) => Promise<void>

  @Action('paymentMethods/removeAllowedPaymentMethod')
  removeAllowedPaymentMethod!: (id: number) => Promise<void>

  @Mutation(UPDATE_WEBSOCKET_STATUS)
  updateStatus!: (status: HubConnectionState) => void

  destroyed () {
    this.closeConnection()
  }

  async openConnection () {
    if (this.connection !== null) {
      await this.closeConnection()
    }

    const connection = api.webSocket.createConnection(this.tokenV2 as string)

    connection.onclose(() => { this.updateStatus(connection.state) })
    connection.onreconnected(() => { this.updateStatus(connection.state) })
    connection.onreconnecting(() => { this.updateStatus(connection.state) })

    for (const [methodName, eventName] of this.invoiceEvents) {
      connection.on(methodName, (data) => this.updateInvoice(eventName, data))
    }

    for (const [methodName, eventName] of this.orderEvents) {
      connection.on(methodName, (data) => this.updateOrder(eventName, data))
    }

    for (const [methodName, eventName] of this.paymentMethodEvents) {
      if (eventName === 'paymentMethodRemoved') {
        connection.on(methodName, (data) => this.removeAllowedPaymentMethod(data.id))
      } else {
        connection.on(methodName, (data) => this.loadAllowedPaymentMethod(data.id))
      }
    }

    await connection.start()
    await connection.invoke('account')

    this.updateStatus(connection.state)

    this.connection = connection
  }

  async closeConnection () {
    const connection = this.connection

    if (connection !== null) {
      await connection.stop()

      for (const [methodName] of this.invoiceEvents) {
        connection.off(methodName)
      }

      for (const [methodName] of this.orderEvents) {
        connection.off(methodName)
      }

      this.connection = null
    }
  }

  async updateInvoice (eventName: string, data: { id: number }) {
    this.$emit(eventName, data.id)
    await this.loadInvoice(data.id)
  }

  async updateOrder (eventName: string, data: { id: number }) {
    this.$emit(eventName, data.id)
    await this.loadOrder(data.id)
  }

  render (createElement: () => void) {
    return createElement()
  }
}
